import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';

/**
 * A price range in one currency - "66.11 EUR - 108.80 EUR" - with each amount leading and its
 * currency code a step smaller, so the number reads as the price and the code trails as a label.
 *
 * Shared by the product page intro and the product grid card: the two differ in size and colour,
 * so each passes its own `amountSx`/`currencySx`, but the shape stays the same everywhere.
 * A single-price product passes one amount and gets the same treatment without the dash.
 */
const PriceRangeLine = ({ amounts, code, amountSx, currencySx, justifyContent = 'flex-start' }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'baseline',
      flexWrap: 'wrap',
      columnGap: 0.75,
      justifyContent
    }}>
    {amounts.map((amount, index) => (
      // Keyed by position: the two ends of a range can hold the same amount when every variation
      // of a product costs the same, and the amount alone would then collide.
      <React.Fragment key={`${code}-${index}`}>
        {index > 0 && (
          <Box component="span" aria-hidden="true" sx={{ color: 'text.disabled' }}>
            -
          </Box>
        )}
        <Typography component="span" sx={amountSx}>
          {amount}{' '}
          <Box component="span" sx={currencySx}>
            {code}
          </Box>
        </Typography>
      </React.Fragment>
    ))}
  </Box>
);

PriceRangeLine.propTypes = {
  amounts: PropTypes.arrayOf(PropTypes.string).isRequired,
  code: PropTypes.string.isRequired,
  amountSx: PropTypes.object.isRequired,
  currencySx: PropTypes.object.isRequired,
  justifyContent: PropTypes.string
};

export default PriceRangeLine;
